import { Injectable } from "@angular/core";
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RoutesRecognized,
} from "@angular/router";
import { BehaviorSubject } from "rxjs";

import { InvalidOperationException } from "../exceptions/exceptions";
import { CustomRouteData } from "../routes/shared/shared-route.constant";

import { DataAnalyticsEventPublisherService } from "./data-analytics/data-analytics-event-publisher.service";
import { Language } from "./language/language.enum";
import { LanguageService } from "./language/language.service";
import { DocumentObjectModelService } from "./document-object-model.service";

@Injectable({
  providedIn: "root",
})
export class BackgroundSubscriptionNavigationService {
  private _isNavigationOngoing$ = new BehaviorSubject(false);
  public isNavigationOngoing$ = this._isNavigationOngoing$.asObservable();

  private _hasNavigationStarted$ = new BehaviorSubject(false);
  public hasNavigationStarted$ = this._hasNavigationStarted$.asObservable();

  constructor(
    private readonly _router: Router,
    private readonly _languageService: LanguageService,
    private readonly _documentObjectModelService: DocumentObjectModelService,
    private readonly _dataAnalyticsEventPublisherService: DataAnalyticsEventPublisherService,
  ) {}

  start() {
    this.subscribeToNavigationEvents();
  }

  private subscribeToNavigationEvents() {
    this._router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.publishNavigationStartedEvent();
        this._languageService.updateHtmlTagLanguage();

        if (!this.isNavigationOngoing()) {
          this.dispatchIsNavigationOngoingChange(true);
        }
      }

      if (event instanceof NavigationEnd) {
        if (this.isNavigationOngoing()) {
          this.dispatchIsNavigationOngoingChange(false);
        }
      }

      if (event instanceof NavigationCancel) {
        if (this.isNavigationOngoing()) {
          this.dispatchIsNavigationOngoingChange(false);
        }
      }

      if (event instanceof NavigationError) {
        if (this.isNavigationOngoing()) {
          this.dispatchIsNavigationOngoingChange(false);
        }
      }

      if (event instanceof RoutesRecognized) {
        const customRouteData = this.extractCustomRouteData(event);
        this.setPageSpecificContent(customRouteData);
        this.pushVirtualPageViewEvent(event);
      }
    });
  }

  private setPageSpecificContent(customRouteData: CustomRouteData) {
    this._documentObjectModelService.setDocumentLanguage(Language.DE);
    this._documentObjectModelService.setHtmlTitle(customRouteData.htmlTitle);
    this.setDescriptionMetaTag(customRouteData);
  }

  private setDescriptionMetaTag(customRouteData: CustomRouteData) {
    /**
     * We should always set a value for the description meta tag, otherwise our lighthouse tests will report an error
     */
    const descriptionMetaTagValue =
      customRouteData.pageSubtitle ??
      customRouteData.pageTitle ??
      "No Description";

    this._documentObjectModelService.setDescriptionMetaTag(
      descriptionMetaTagValue,
    );
  }

  private extractCustomRouteData(event: RoutesRecognized) {
    const route = event.state.root.firstChild;

    if (route) {
      const customRouteData = route.data as CustomRouteData;
      return customRouteData;
    } else {
      throw new InvalidOperationException(
        "Every route from our application should have 'CustomRouteData' assigned to it",
      );
    }
  }

  private publishNavigationStartedEvent() {
    this._hasNavigationStarted$.next(true);
  }

  private dispatchIsNavigationOngoingChange(isNavigationOngoing: boolean) {
    this._isNavigationOngoing$.next(isNavigationOngoing);
  }

  private isNavigationOngoing() {
    return this._isNavigationOngoing$.value;
  }

  private pushVirtualPageViewEvent(event: RoutesRecognized) {
    this._dataAnalyticsEventPublisherService.pushVirtualPageViewEvent(
      event.urlAfterRedirects,
    );
  }
}
